import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronRight, Truck, ShieldCheck } from "lucide-react";
import { CATEGORIES } from "@/data/products";

const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

function CategoriesPanel({ catalog, onNavigate }) {
  const all = catalog.categories || [];
  const roots = all.filter((c) => !c.parent_id);
  const [active, setActive] = useState(null);
  const activeRoot = roots.find((r) => r.id === active) || roots[0];
  const subs = all.filter((c) => c.parent_id === activeRoot?.id);

  if (!roots.length) {
    return <p className="px-7 py-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-fern">Loading rituals from HQ…</p>;
  }

  return (
    <div className="flex">
      <div className="w-56 shrink-0 border-r border-champagne/30 bg-moss/40 py-3">
        {roots.map((r) => {
          const count = all.filter((c) => c.parent_id === r.id).length;
          const isActive = activeRoot?.id === r.id;
          return (
            <button
              key={r.id}
              data-testid={`mega-root-${r.id}`}
              onMouseEnter={() => setActive(r.id)}
              onClick={() => setActive(r.id)}
              className={`flex w-full items-center justify-between px-6 py-4 text-left transition-colors duration-200 ${isActive ? "bg-pine text-gold" : "text-cream hover:bg-pine/60"}`}
            >
              <span>
                <span className="block font-display text-lg tracking-tight">{titleCase(r.name)}</span>
                <span className="mt-0.5 block font-mono text-[8px] uppercase tracking-[0.25em] text-fern">
                  {count ? `${count} rituals` : "direct shelf"}
                </span>
              </span>
              <ChevronRight className={`h-4 w-4 transition-transform ${isActive ? "translate-x-0.5 text-gold" : "text-fern"}`} />
            </button>
          );
        })}
        <button
          data-testid="mega-root-all"
          onClick={() => onNavigate("/shop")}
          className="mt-2 flex w-full items-center gap-2 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-gold hover:underline"
        >
          Shop everything <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      <div className="flex-1 p-3">
        <div className="grid grid-cols-3 gap-1">
          <button
            data-testid={`mega-sub-all-${activeRoot?.id}`}
            onClick={() => onNavigate(`/category/${activeRoot.id}`)}
            className="group flex items-center gap-4 rounded-2xl border border-dashed border-gold/40 bg-gold/5 p-3.5 text-left transition-colors duration-300 hover:bg-gold/15"
          >
            <div className="min-w-0">
              <p className="truncate font-display text-base text-gold">All {titleCase(activeRoot.name)}</p>
              <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-sage">The complete shelf</p>
            </div>
            <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-gold" />
          </button>
          {subs.map((c, i) => (
            <button
              key={c.id}
              data-testid={`mega-category-${c.id}`}
              onClick={() => onNavigate(`/category/${c.id}`)}
              className="group flex items-center gap-4 rounded-2xl p-3.5 text-left transition-colors duration-300 hover:bg-moss"
            >
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-champagne/40">
                <img src={CATEGORIES[i % CATEGORIES.length].img} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              </div>
              <div className="min-w-0">
                <p className="truncate font-display text-base text-cream transition-colors group-hover:text-gold">{titleCase(c.name)}</p>
                <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-fern">
                  {c.product_count ? `${c.product_count} products` : "Stocking soon"}
                </p>
              </div>
              <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-gold opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          ))}
          {subs.length === 0 && (
            <p className="col-span-2 px-4 py-8 font-mono text-[10px] uppercase tracking-[0.2em] text-fern">
              No sub-rituals yet — shop the whole shelf
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MegaMenu({ type, catalog, onNavigate, onKeepOpen, onScheduleClose }) {
  const isCats = type === "categories";
  const brands = catalog.brands || [];

  return (
    <motion.div
      data-testid={`mega-menu-${type}`}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={onKeepOpen}
      onMouseLeave={onScheduleClose}
      className="absolute inset-x-0 top-full hidden pt-2 lg:block"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="overflow-hidden rounded-3xl border border-champagne/50 bg-pine/95 shadow-2xl shadow-gold/15 backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-champagne/30 px-7 py-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              {isCats ? "Shop by Ritual" : "Shop by House"}
            </p>
            <button
              data-testid={`mega-view-all-${type}`}
              onClick={() => onNavigate(isCats ? "/shop" : "/#brands")}
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-sage transition-colors hover:text-gold"
            >
              {isCats ? "View full shelf" : "View brand index"} <ArrowRight className="h-3 w-3" />
            </button>
          </div>

          {isCats ? (
            <CategoriesPanel catalog={catalog} onNavigate={onNavigate} />
          ) : brands.length === 0 ? (
            <p className="px-7 py-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-fern">Loading houses from HQ…</p>
          ) : (
            <div className="grid grid-cols-4 gap-px bg-champagne/30">
              {brands.slice(0, 8).map((b) => (
                <button
                  key={b.id}
                  data-testid={`mega-brand-${b.id}`}
                  onClick={() => onNavigate(`/shop?brand_id=${b.id}`)}
                  className="group bg-pine p-6 text-left transition-colors duration-300 hover:bg-moss"
                >
                  <p className="font-display text-xl tracking-tight text-cream transition-colors group-hover:text-gold">{titleCase(b.name)}</p>
                  <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-fern">
                    {b.product_count ? `${b.product_count} products` : "Stocking soon"}
                  </p>
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 border-t border-champagne/30 bg-moss/60 px-7 py-3.5">
            <p className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-sage">
              <Truck className="h-3.5 w-3.5 text-gold" /> Free shipping over ₹999
            </p>
            <p className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-sage">
              <ShieldCheck className="h-3.5 w-3.5 text-gold" /> 100% authentic from HQ
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
